import { useEffect } from 'react';
import { OG_IMAGE, SITE_ORIGIN, seoFor } from '../data/seo';
const MANAGED = 'data-seo-managed';
function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => el!.setAttribute(key, value));
}
function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  el.href = href;
}

/**
 * Per-route head management: title, description, canonical, social cards, and
 * the structured data blocks. The document shell is fixed, so these are written
 * at runtime — the JSON-LD is what answer engines read for the FAQ and
 * organization entities.
 */
export function Seo({
  pathname
}: {
  pathname: string;
}) {
  useEffect(() => {
    const page = seoFor(pathname);
    const url = `${SITE_ORIGIN}${page.path}`;
    // An article shares its own hero art rather than the generic site card.
    const image = page.image ?? OG_IMAGE;
    document.title = page.title;
    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: page.description
    });
    upsertLink('canonical', url);
    const og: Record<string, string> = {
      'og:type': page.ogType ?? 'website',
      'og:site_name': 'Synthia',
      'og:title': page.ogTitle ?? page.title,
      'og:description': page.ogDescription ?? page.description,
      'og:url': url,
      'og:image': image
    };
    Object.entries(og).forEach(([property, content]) => {
      upsertMeta(`meta[property="${property}"]`, {
        property,
        content
      });
    });
    const twitter: Record<string, string> = {
      'twitter:card': 'summary_large_image',
      'twitter:title': page.ogTitle ?? page.title,
      'twitter:description': page.ogDescription ?? page.description,
      'twitter:image': image
    };
    Object.entries(twitter).forEach(([name, content]) => {
      upsertMeta(`meta[name="${name}"]`, {
        name,
        content
      });
    });

    // Structured data is replaced wholesale per route rather than patched, so a
    // previous page's FAQ entities can never linger on the next one.
    document.head.querySelectorAll(`script[type="application/ld+json"][${MANAGED}]`).forEach(node => node.remove());
    const scripts = page.jsonLd.map(block => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(MANAGED, '');
      script.textContent = JSON.stringify(block);
      document.head.appendChild(script);
      return script;
    });
    return () => scripts.forEach(script => script.remove());
  }, [pathname]);
  return null;
}